/**
 * Receive screen (procurement.md §2 Receive, §4): count what arrived per line and record each serialized unit's
 * serial (scanned by barcode on the phone, or typed), model and MAC. Partial receipts are allowed; each save is one
 * `receive(order_line_id, qty, units[])`. Phone-first: big targets, one line at a time. Owner: web-procurement.
 */
import { Button, Chip, EmptyState, ErrorBanner, Input, SkeletonRows, toast } from '@patchbay/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import * as React from 'react'
import { Frame } from '../../components/frame'
import { describeError } from '../../lib/api/client'
import { poText } from '../../lib/po'
import { type HardwareOrderLineOut, type HardwareOrderOut, procurementApi, procurementKeys, type UnitIn } from './api'
import { SerialField } from './scan'

interface UnitDraft {
  key: number
  serial: string
  model: string
  mac: string
}

let nextKey = 1

/** Problems that block a save, in plain words. Exported for tests. */
export function unitProblems(units: UnitDraft[], existingSerials: string[]): string[] {
  const out: string[] = []
  const seen = new Set(existingSerials.map((s) => s.trim().toUpperCase()))
  units.forEach((u, i) => {
    const serial = u.serial.trim().toUpperCase()
    if (!serial) out.push(`Unit ${i + 1} has no serial`)
    else if (seen.has(serial)) out.push(`Serial ${u.serial.trim()} is already recorded`)
    else seen.add(serial)
    if (u.mac.trim() && !/^(?:[0-9a-f]{2}[:-]?){5}[0-9a-f]{2}$/i.test(u.mac.trim())) out.push(`Unit ${i + 1} MAC is not 12 hex digits`)
  })
  return out
}

function LineReceive({ order, line }: { order: HardwareOrderOut; line: HardwareOrderLineOut }) {
  const qc = useQueryClient()
  const remaining = Math.max(0, line.qty - line.qty_received)
  const [units, setUnits] = React.useState<UnitDraft[]>([])
  const [count, setCount] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const location: 'office' | 'site' = order.ship_to.kind === 'office' ? 'office' : 'site'
  const existing = (order.units ?? []).map((u) => u.serial ?? '').filter(Boolean)

  const addUnit = () =>
    setUnits((us) => (us.length >= remaining ? us : [...us, { key: nextKey++, serial: '', model: line.model ?? '', mac: '' }]))
  const patch = (key: number, p: Partial<UnitDraft>) => setUnits((us) => us.map((u) => (u.key === key ? { ...u, ...p } : u)))

  const problems = line.is_serialized ? unitProblems(units, existing) : []
  const qty = line.is_serialized ? units.length : Number(count)
  const valid = Number.isInteger(qty) && qty >= 1 && qty <= remaining && problems.length === 0

  const save = async () => {
    setSaving(true)
    try {
      const body: UnitIn[] = line.is_serialized
        ? units.map((u) => ({ serial: u.serial.trim(), model: u.model.trim() || null, mac: u.mac.trim() || null }))
        : []
      await procurementApi.receive(line.id, { qty, location, units: body })
      setUnits([])
      setCount('')
      await Promise.all([
        qc.invalidateQueries({ queryKey: procurementKeys.order(order.id) }),
        qc.invalidateQueries({ queryKey: procurementKeys.case(order.case_id) }),
      ])
      toast.success(`Received ${qty} × ${line.product_name}`)
    } catch (error) {
      toast.error(`Receipt not saved: ${describeError(error)}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-3" aria-label={`Receive ${line.product_name}`}>
      <header className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-semibold">{line.product_name}</h2>
        <Chip tone={remaining === 0 ? 'success' : line.qty_received ? 'warning' : 'outline'}>
          {line.qty_received}/{line.qty} received
        </Chip>
        {line.model ? <span className="text-xs text-muted-foreground">model {line.model}</span> : null}
      </header>
      {remaining === 0 ? (
        <p className="text-sm text-muted-foreground">Everything on this line is in.</p>
      ) : line.is_serialized ? (
        <>
          {units.map((u, i) => (
            <div key={u.key} className="space-y-2 rounded-md border border-border p-2.5" data-unit={i + 1}>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Unit {i + 1}</span>
                <Button
                  size="icon-sm"
                  variant="ghost"
                  className="ml-auto"
                  aria-label={`Remove unit ${i + 1}`}
                  onClick={() => setUnits((us) => us.filter((x) => x.key !== u.key))}
                >
                  <Trash2 />
                </Button>
              </div>
              <SerialField
                label={`Serial for unit ${i + 1}`}
                value={u.serial}
                onChange={(v) => patch(u.key, { serial: v })}
                onScanMac={(v) => patch(u.key, { mac: v })}
                autoFocus={i === units.length - 1}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  aria-label={`Model for unit ${i + 1}`}
                  placeholder="Model"
                  value={u.model}
                  onChange={(e) => patch(u.key, { model: e.currentTarget.value })}
                  className="h-11"
                />
                <Input
                  aria-label={`MAC for unit ${i + 1}`}
                  placeholder="MAC (if any)"
                  value={u.mac}
                  autoCapitalize="characters"
                  onChange={(e) => patch(u.key, { mac: e.currentTarget.value })}
                  className="h-11 font-mono"
                />
              </div>
            </div>
          ))}
          <Button variant="outline" className="h-11 w-full" onClick={addUnit} disabled={units.length >= remaining}>
            <Plus /> Add a unit ({remaining - units.length} still expected)
          </Button>
        </>
      ) : (
        <label className="flex items-center gap-2 text-sm">
          Quantity received now
          <Input
            aria-label={`Quantity received of ${line.product_name}`}
            inputMode="numeric"
            className="h-11 w-24"
            value={count}
            onChange={(e) => setCount(e.currentTarget.value)}
          />
          <span className="text-muted-foreground">of {remaining}</span>
        </label>
      )}
      {problems.length ? (
        <ul className="text-sm text-destructive">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
      {remaining > 0 ? (
        <Button variant="primary" className="h-11 w-full" disabled={!valid || saving} onClick={() => void save()}>
          Save receipt{qty >= 1 && Number.isInteger(qty) ? ` (${qty})` : ''}
        </Button>
      ) : null}
    </section>
  )
}

export function ReceiveScreen({ orderId }: { orderId: string }) {
  const q = useQuery({ queryKey: procurementKeys.order(orderId), queryFn: () => procurementApi.order(orderId), retry: 1 })
  let body: React.ReactNode
  if (q.isPending) body = <SkeletonRows count={4} />
  else if (q.isError)
    body = (
      <ErrorBanner title="Order unreachable" detail={`${describeError(q.error)}. Nothing was recorded.`} onRetry={() => void q.refetch()} />
    )
  else if (q.data.lines.length === 0)
    body = <EmptyState title="This order has no lines." detail="The order loaded; it has nothing to receive." />
  else
    body = (
      <div className="space-y-3">
        {q.data.lines.map((l) => (
          <LineReceive key={l.id} order={q.data} line={l} />
        ))}
      </div>
    )
  return (
    <Frame
      active="ledger"
      railless
      main={
        <div className="mx-auto flex h-full w-full max-w-xl flex-col gap-3 overflow-y-auto px-4 py-4">
          <div className="flex items-center gap-2">
            {q.data ? (
              <Link
                to="/case/$id"
                params={{ id: q.data.case_number ?? q.data.case_id }}
                className="inline-flex items-center gap-1 text-sm text-primary"
              >
                <ArrowLeft className="size-4" /> Case {q.data.case_number ?? ''}
              </Link>
            ) : null}
          </div>
          <h1 className="text-xl font-semibold">Receive hardware</h1>
          {q.data ? (
            <p className="text-sm text-muted-foreground">
              {q.data.vendor_name ?? 'Vendor'} · {poText(q.data.purchase_order?.books_po_number) || 'PO not issued'} ·{' '}
              {q.data.ship_to.kind === 'office' ? 'received at the office' : 'received on site'}
            </p>
          ) : null}
          {body}
        </div>
      }
    />
  )
}
